import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import DeliveryDining from '@mui/icons-material/DeliveryDining';
import {
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Link,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import { DataGrid } from '@mui/x-data-grid';
import React, { Fragment, useState } from 'react';
import { AlertCircle } from 'react-feather';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Alert } from 'reactstrap';
import * as Yup from 'yup';
import { userRequest } from '../../../auth/requestMethods';
import Breadcrumb from '../../common/breadcrumb';
import DateRange from '../../dateRange';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';

const days = { 1: 'Mo', 2: 'Tu', 3: 'We', 4: 'Th', 5: 'Fr' };

/* 
history -> conatins methods and information related to browser history. 
It provides methods like push, replace, and goBack to control the navigation. 
You can use history to programmatically navigate to a different URL or modify 
the browser history.

location -> location is an object that represents the current URL location. 
It contains information about the current URL path, search parameters, hash, 
and other details related to the URL. You can access properties like pathname, 
search, hash, and state from the location object to get information about the 
current URL. 
*/

/**
 * Manage component for handling delivery run creation and editing.
 *
 * This component provides a form interface for creating and editing delivery runs.
 * It manages various states and effects related to delivery run data, form validation,
 * and UI interactions. The component fetches necessary data, handles form submissions,
 * and provides feedback to users regarding delivery run status and potential issues.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.history - Browser history object for navigation.
 * @param {Object} props.location - Location object containing URL information.
 * @returns {ReactElement} - The rendered Manage component.
 */
const Manage = ({ history, location }) => {
  const [errorModalOpen, setErrorModalOpen] = React.useState(false);
  const [skusMissingAccountingCodes, setSkuMissingAccountCodes] = React.useState([]);
  const [missingSkus, setMissingSkus] = React.useState([]);
  const [disableExporting, setDisableExporting] = React.useState(false);
  const [isFetchingAccountingDbs, setIsFetchingAccountingDbs] = React.useState(true);
  const [accountingDbs, setAccountingDbs] = React.useState([]);
  React.useEffect(getDatabases, []);

  const [filterOptions, setFilterOptions] = React.useState({
    terms: [],
    publicHolidays: [],
    schoolCalendars: [],
    deliveryRunTypes: [],
  });
  const [deliveryRunId, setDeliveryRunId] = React.useState(0);
  const [existingDeliveryRunWarning, setExistingDeliveryRunWarning] = React.useState(false);
  // const [exceptionOverlapError, setExceptionOverlapError] = React.useState(false);
  const [tableData, setTableData] = React.useState({
    packSizes: [],
    current: null,
    schools: [],
  });

  //used to display the fact that the UI is making a request
  const [isFetchingDeliveryRunData, setIsFecthingDeliveryRunData] = React.useState(false);

  const [currentDeliveryRun, setCurrentDeliveryRun] = React.useState(undefined);

  //destructuring the useForm hook
  const {
    register,
    handleSubmit,
    watch,
    control,
    trigger,
    formState,
    getValues,
    setValue,
    reset,
    formState: { errors, isSubmitted },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  //values is set to the object from the useForm hook
  const values = getValues();

  /**
   * Memoized value that represents whether there is a date error.
   * It checks if the planned delivery dates overlap with the consumption start date.
   *
   * @type {boolean}
   */
  const dateError = React.useMemo(() => {
    // Convert the dates to the same day at 00:00:00
    let date = new Date(values.consumption_start_date).setHours(0, 0, 0, 0);
    let end_date = new Date(values.planned_delivery_end_date).setHours(0, 0, 0, 0);
    let start_date = new Date(values.planned_delivery_start_date).setHours(0, 0, 0, 0);

    // Check if either the planned delivery end date or the planned delivery start date is greater than the consumption start date
    if (start_date > date || end_date > date) {
      return true;
    } else return false;
  }, [values.planned_delivery_end_date, values.planned_delivery_start_date, values.consumption_start_date]);

  /**
   * Memoized value that represents the calculations based on the form values.
   * It calculates school holidays if all conditions are met, otherwise returns default calculations.
   *
   * @type {object}
   */
  const calculations = React.useMemo(() => {
    // If there is a date error or any required form field is empty or filter options do not have any terms
    if (dateError || filterOptions.terms.length < 1 || !Object.values(values).every((v) => v !== '')) {
      // If calculations are already set to default calculations, no need to recalculate
      if (calculations === defaultCalcultions) {
      } // Return default calculations
      else return defaultCalcultions;
    } else {
      let t = filterOptions.terms;
      let ph = filterOptions.publicHolidays;
      // Calculate school holidays based on filter options and consumption dates
      return getSchoolHolidays({ ph, t }, values.consumption_start_date, values.consumption_end_date);
    }
  }, [filterOptions, values, dateError]);

  const schoolHolidaysOverlapWarning = React.useMemo(() => {
    let start_date = values.planned_delivery_start_date,
      end_date = values.planned_delivery_end_date,
      ph = filterOptions.publicHolidays,
      t = filterOptions.terms;

    if (start_date && end_date && ph.length && t.length) {
      let { schoolHolidays } = getSchoolHolidays({ ph, t }, start_date, end_date);
      let found = schoolHolidays.findIndex((val) => {
        let date = new Date(val.date).setHours(0, 0, 0, 0);
        start_date = new Date(start_date).setHours(0, 0, 0, 0);
        end_date = new Date(end_date).setHours(0, 0, 0, 0);

        return date >= start_date && date <= end_date;
      });

      if (found < 0) return false;
      else return true;
    }
    return false;
  }, [
    values.planned_delivery_end_date,
    values.planned_delivery_start_date,
    filterOptions.publicHolidays,
    filterOptions.terms,
  ]);

  const handleExport = async () => {
    try {
      exportToSage();
      // Handle success (e.g., show a success message or update UI)
    } catch (error) {
      console.error('Export failed:', error);
      // Handle error (e.g., show an error message)
    }
  };

  const disableButton = React.useMemo(() => {
    if (!formState.isValid) {
      return true;
    }
    if (!!currentDeliveryRun?.updated_tables?.length) {
      return false;
    }
    if (!formState.isDirty) {
      return true;
    }

    return false;
  }, [formState, currentDeliveryRun?.updated_tables?.length]);

  //*  Fetches existing delivery runs based on form field values and updates the existingDeliveryRunWarning state.
  React.useEffect(() => {
    let q = ['consumption_start_date', 'consumption_end_date', 'delivery_run_type_id'];

    // Check if the relevant form fields are dirty and have values
    if (
      formState.dirtyFields?.consumption_end_date &&
      formState.dirtyFields?.consumption_start_date &&
      q.every((v) => values[v])
    ) {
      // Build the query string with the selected form field values
      let query = q
        .map((val, ind) => `${val}=${ind === 2 ? values[val] : dateToFormattedString(new Date(values[val]))}`)
        .join('&');

      // Send a GET request to fetch existing delivery runs based on the query
      userRequest()
        .get(`/delivery-runs/existing-delivery-runs?${query}`)
        .then(({ data }) => {
          // Check if there are existing delivery runs
          if (data.total > 0) setExistingDeliveryRunWarning(true);
          else setExistingDeliveryRunWarning(false);
        })
        .catch((error) => console.log(error));
    }
    // If the relevant form fields are not dirty or do not have values, reset the existingDeliveryRunWarning state
    else existingDeliveryRunWarning && setExistingDeliveryRunWarning(false);
    //rerun when any of the below values are changed
  }, [
    values.delivery_run_type_id,
    values.consumption_start_date,
    values.consumption_end_date,
    formState.dirtyFields?.consumption_end_date,
    formState.dirtyFields?.consumption_start_date,
    existingDeliveryRunWarning,
    values,
  ]);

  //Executes form validation on initial load and sets up subscriptions for watching form field changes.
  React.useEffect(() => {
    // Trigger form validation on initial load
    trigger();
    // Subscribe to form field changes and trigger validation for the corresponding field
    const subscription = watch((value, { name }) => trigger(name));
    // Unsubscribe from the form field change subscription when the component is unmounted
    return () => subscription.unsubscribe();
    //rerun when any of the below values change
  }, [watch, trigger]);

  React.useEffect(() => {
    //check if the location.state property exists
    if (location.state) {
      //if it does, then destructure it
      const { response, filterOptions } = location.state;

      // Destructure the deliveryRun object from the response
      let { id, ...rest } = response.deliveryRun;

      // Set filterOptions state with the provided filterOptions
      setFilterOptions(filterOptions);

      reset(rest);
      setDeliveryRunId(id);
      setTableData((prev) => {
        if (response.schoolPackSizes.length > 0) prev.current = 0;
        prev.packSizes = response.schoolPackSizes;
        prev.schools = response.schools;
        return { ...prev };
      });
    } else {
      //set temp to an array of strings based on the current URL
      let temp = location.pathname.split('/');
      let promises = ['/school-calendars/names', '/public-holidays', '/delivery-run-types/names'];

      //if this is an edit page, add the info to the list of requests
      if (temp[2] === 'edit') {
        //set the ID of the delivery run
        setDeliveryRunId(temp[3]);
        //add the request of school-calendars terms to the list of requests
        promises.push('/school-calendars/terms');
        //add the request for the delivery runs with the specified id
        promises.push('/delivery-runs/retrieve/' + temp[3]);
      }

      //about to make request, update UI to show data is being fetched
      setIsFecthingDeliveryRunData(true);

      // Make multiple requests using bulkAxios function
      bulkAxios(promises)
        .then((all) => {
          // Set filterOptions state with the received data
          setFilterOptions({
            schoolCalendars: all[0].schoolCalendars,
            publicHolidays: all[1].publicHolidays,
            deliveryRunTypes: all[2].deliveryRunTypeNames,
            terms: all.length > 3 ? all[3].terms : [],
          });
          if (all.length > 4) {
            let { id, ...rest } = all[4].deliveryRun;
            // Reset the form with the retrieved deliveryRun data
            reset(rest);

            setCurrentDeliveryRun(rest);

            // Update tableData state with the retrieved data
            setTableData((prev) => {
              if (all[4].schoolPackSizes.length > 0) prev.current = 0;
              prev.packSizes = all[4].schoolPackSizes;
              prev.schools = all[4].schools;
              return { ...prev };
            });
          }
          //request done, update UI to show data is not being fetched
          setIsFecthingDeliveryRunData(false);
        })
        .catch((error) => {
          //log any errors if needed
          console.log(error);
          //request done, update UI to show data is not being fetched
          setIsFecthingDeliveryRunData(false);
        });
    }
    console.log(filterOptions);
  }, [location.pathname, location.state, reset]);

  function bulkAxios(urls = null) {
    if (!urls) return urls;

    let promises = urls.map((req) =>
      userRequest()
        .get(req)
        .then(({ data }) => {
          return data;
        })
        .catch((error) => {
          console.log(error);
          return null;
        })
    );

    return Promise.all(promises);
  }

  /**
   * Changes the school calendar and updates the filter options based on the selected value.
   * @function changeSchoolCalendar
   * @param {Object} field - The form field object.
   * @param {Event} e - The event object representing the change event.
   */
  function changeSchoolCalendar(field, e) {
    // Make a user request to fetch terms for the selected school calendar
    userRequest()
      .get('/school-calendars/terms?school_calendar_id=' + e.target.value)
      .then(({ data }) => {
        // Update the filter options with the new terms data
        setFilterOptions((prev) => {
          return {
            ...prev,
            terms: data.terms,
          };
        });
        // Update the field value with the selected school calendar ID
        field.onChange(e.target.value);
      })
      .catch((error) => {
        // Handle any errors that occur during the API request
        console.error(error);
      });
  }

  function getDatabases() {
    setIsFetchingAccountingDbs(true);
    userRequest()
      .get('/accounting-databases/')
      .then(({ data }) => {
        setAccountingDbs(data.accountingDatabases ?? []);
        setIsFetchingAccountingDbs(false);
      })
      .catch((error) => console.log(error));
  }

  /**
   * Handles exporting a delivery run to Sage.
   * @function exportToSage
   * @since 1.0.0
   * @returns {void}
   */
  function exportToSage() {
    toast.info('Exporting...', {
      isLoading: true,
      position: 'bottom-center',
      autoClose: 5000,
      theme: 'colored',
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      toastId: 'export',
    });

    setDisableExporting(true);

    userRequest()
      .post('/delivery-runs/export/' + deliveryRunId)
      .then(({ data }) => {
        if (data.response.hasErrors) {
          const error = data.response.error;
          setErrorModalOpen(true);
          setSkuMissingAccountCodes(error.missingSkuAccountingCodes);
          setMissingSkus(error.missingSkus);
        } else {
          toast.success('Export was successful!', {
            position: 'bottom-center',
            autoClose: 5000,
            closeOnClick: true,
            draggable: true,
            toastId: 'export-success',
          });
        }
        toast.dismiss('export');
        setDisableExporting(false);
      })
      .catch((err) => {
        toast.dismiss('export');
        toast.error('An unexpected error occurred.', {
          position: 'bottom-center',
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
          toastId: 'export-unexpected-error',
        });
        setDisableExporting(false);
      });
  }

  /**
   * Sets the date range for a given form field.
   * @param {string} name - The name of the form field.
   * @param {string} val - The value of the form field.
   */
  function setDateRange(name, val) {
    let date = val ? (!isNaN(Date.parse(val)) ? dateToFormattedString(new Date(val)) + 'T00:00:00.000Z' : '') : '';
    setValue(name, date, { shouldDirty: true, shouldTouch: true });
    // setValue(name, date), { shouldDirty: true, shouldTouch: true });
  }

  function goToDetails() {
    history.push('/reports/delivery-run-details/' + deliveryRunId);
  }

  function onSave(form) {
    if (holidayWarningOpen) setHolidayWarningOpen(false);

    toast.info(`${deliveryRunId ? 'Updating...' : 'Creating...'}`, {
      isLoading: true,
      position: 'bottom-center',
      autoClose: 5000,
      theme: 'colored',
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      toastId: 'create',
    });

    let api = deliveryRunId ? `/delivery-runs/update/${deliveryRunId}` : '/delivery-runs';
    let formData = deliveryRunId
      ? Object.keys(formState.dirtyFields).reduce((prev, key) => {
          prev[key] = values[key];
          return prev;
        }, {})
      : form;

    userRequest()
      .post(api, { ...formData, terms: filterOptions.terms, publicHolidays: filterOptions.publicHolidays })
      .then(({ data }) => {
        toast.dismiss('create');
        toast.success(`Successfully ${deliveryRunId ? 'updated' : 'created'}`, {
          position: 'bottom-center',
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
        });
        history.replace('/delivery-runs/edit/' + data.deliveryRun.id, {
          filterOptions,
          response: data,
        });
      })
      .catch(({ response }) => {
        toast.dismiss('create');
        toast.error(response.data.message, {
          position: 'bottom-center',
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
        });
      });
  }

  const schoolDays = {
    id: 6,
    inc: Object.values(calculations.include).reduce((prev, val) => prev + val, 0),
    exc: '',
    day: 'School Days',
  };

  const [holidayWarningOpen, setHolidayWarningOpen] = useState(false);

  const formattedSchoolRunBreakdown = tableData.packSizes.reduce((acc, row) => {
    const {
      schoolId,
      schoolName,
      project,
      projectId,
      exception,
      exception_id,
      menuName,
      menuId,
      temporary_learners_fraction,
      exceptionMenu,
      exceptionSkuType,
    } = row;

    let school = acc.find((s) => s.schoolId === schoolId);

    if (!school) {
      school = {
        schoolId,
        schoolName,
        project,
        projectId,
        exceptions: [],
        menus: [],
        menuIds: new Set(),
        exceptionIds: new Set(),
      };
      acc.push(school);
    }

    if (!school.exceptionIds.has(exception_id)) {
      school.exceptions.push({
        exception,
        exception_id,
        learnerFrac: temporary_learners_fraction,
        menu: exceptionMenu,
        exceptionSkuTypes: exceptionSkuType ? [exceptionSkuType] : [],
      });
      school.exceptionIds.add(exception_id);
    } else {
      const ex = school.exceptions.find((ex) => ex.exception_id === exception_id);
      if (ex && exceptionSkuType) {
        ex.exceptionSkuTypes.push(exceptionSkuType);
      }
    }

    if (!school.menuIds.has(menuId)) {
      school.menus.push({ menuName, menuId });
      school.menuIds.add(menuId);
    }

    return acc;
  }, []);

  return (
    <Fragment>
      <Breadcrumb icon={<DeliveryDining />} title={deliveryRunId ? 'Edit' : 'Create'} parent="Delivery Runs" />
      <Helmet>
        <title>PFSA | Delivery Runs | {deliveryRunId ? 'Edit' : 'Create'}</title>
      </Helmet>
      {isFetchingDeliveryRunData && <LinearProgress />}
      <div className="col-sm-12">
        <div className="card">
          <div className="card-body">
            <form className="theme-form" onSubmit={handleSubmit(onSave)}>
              <div className="form-group row">{!!deliveryRunId && <h5>ID: {deliveryRunId}</h5>}</div>
              <div className="form-group row">
                <Controller
                  control={control}
                  name="database"
                  defaultValue={0}
                  render={({ field }) => (
                    <FormControl error={!!(errors.database?.message && isSubmitted && getValues().database === '')}>
                      <InputLabel id="demo-simple-select-helper-label">Delivery Run for Database</InputLabel>
                      <Select
                        name={field.name}
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                        }}
                        labelId="demo-simple-select-helper-label"
                        id="demo-simple-select-helper"
                        label="Delivery Run for Database"
                        disabled={isFetchingAccountingDbs}
                      >
                        <MenuItem value={''}>- Select -</MenuItem>
                        {accountingDbs.map((val, i) => (
                          <MenuItem key={i} value={val}>
                            {val}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </div>

              <div className="form-group row">
                <Controller
                  control={control}
                  name="delivery_run_type_id"
                  defaultValue={0}
                  render={({ field }) => (
                    <FormControl
                      error={
                        !!(
                          errors.delivery_run_type_id?.message &&
                          isSubmitted &&
                          getValues().delivery_run_type_id === ''
                        )
                      }
                    >
                      <InputLabel id="demo-simple-select-helper-label">Delivery Run Type</InputLabel>
                      <Select
                        name={field.name}
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                        }}
                        labelId="demo-simple-select-helper-label"
                        id="demo-simple-select-helper"
                        label="Delivery Run Type"
                        disabled={isFetchingDeliveryRunData}
                      >
                        <MenuItem value={''}>- Select -</MenuItem>
                        {filterOptions.deliveryRunTypes.map((val, i) => (
                          <MenuItem key={i} value={val.id}>
                            {val.display_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </div>
              <br />

              <div className="form-group row">
                <Controller
                  control={control}
                  name="school_calendar_id"
                  defaultValue={0}
                  render={({ field }) => (
                    <FormControl
                      error={
                        !!(errors.school_calendar_id?.message && isSubmitted && getValues().school_calendar_id === '')
                      }
                    >
                      <InputLabel id="demo-simple-select-helper-label">School Calendar</InputLabel>
                      <Select
                        name={field.name}
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={(e) => changeSchoolCalendar(field, e)}
                        labelId="demo-simple-select-helper-label"
                        id="demo-simple-select-helper"
                        label="School Calendar"
                        disabled={isFetchingDeliveryRunData}
                      >
                        <MenuItem value={''}>- Select -</MenuItem>
                        {filterOptions.schoolCalendars.map((val, i) => (
                          <MenuItem key={i} value={val.id}>
                            {val.display_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </div>
              <br />

              {/* /////////////////////// FOR PERIOD //////////////////////// */}

              {/* /////////////////////// PLANNED DELIVERY //////////////////////// */}
              <div className="form-group row">
                {/* <p>Delivery Period</p> */}
                <DateRange
                  fromError={
                    formState.dirtyFields.planned_delivery_start_date
                      ? errors.planned_delivery_start_date?.message
                      : undefined
                  }
                  toError={
                    formState.dirtyFields.planned_delivery_end_date
                      ? errors.planned_delivery_end_date?.message
                      : undefined
                  }
                  fromOnChange={(val) => setDateRange('planned_delivery_start_date', val)}
                  toOnChange={(val) => setDateRange('planned_delivery_end_date', val)}
                  from={values.planned_delivery_start_date}
                  to={values.planned_delivery_end_date}
                  label="Delivery Period"
                  disabled={isFetchingDeliveryRunData}
                />
              </div>

              {console.log(formState.isDirty)}

              <div className="form-group row">
                {/* <p>Consumption Period</p> */}
                <DateRange
                  fromError={
                    formState.dirtyFields.consumption_start_date ? errors.consumption_start_date?.message : undefined
                  }
                  toError={
                    formState.dirtyFields.consumption_end_date
                      ? dateError
                        ? 'Consumption Period cannot start before Delivery Period'
                        : errors.consumption_end_date?.message
                      : undefined
                  }
                  fromOnChange={(val) => setDateRange('consumption_start_date', val)}
                  toOnChange={(val) => setDateRange('consumption_end_date', val)}
                  from={values.consumption_start_date}
                  to={values.consumption_end_date}
                  label="Consumption Period"
                  disabled={isFetchingDeliveryRunData}
                />
                {!!schoolDays.inc && <p>{schoolDays.inc} school days</p>}
              </div>

              <br />
              {/* /////////////////////// School Calendar Details //////////////////////// */}
              {getValues().consumption_start_date && getValues().consumption_end_date && (
                <>
                  <p>
                    <b>School Calendar Details</b>
                  </p>

                  <div className="form-group row">
                    <DataGrid
                      getRowId={(row) => row.schoolId}
                      autoHeight
                      hideFooter
                      loading={false}
                      rows={formattedSchoolRunBreakdown}
                      columns={schoolPackSizesColumns}
                      getRowHeight={(params) => {
                        const numberOfMenus = params.model.menus.length;
                        const numberOfExceptions = params.model.exceptions.length;
                        return 30 + Math.max(numberOfMenus, numberOfExceptions) * 20;
                      }}
                    />
                  </div>

                  {!!existingDeliveryRunWarning && (
                    <div className="form-group row">
                      <Alert className="d-flex gap-3" color="warning dark">
                        <AlertCircle />
                        <p>
                          Warning: There is already a{' '}
                          {
                            filterOptions.deliveryRunTypes.find(({ id }) => id === values.delivery_run_type_id)
                              ?.display_name
                          }{' '}
                          Delivery Run planned for the period{' '}
                          {dateToFormattedString(new Date(values.consumption_start_date))} to{' '}
                          {dateToFormattedString(new Date(values.consumption_end_date))}
                        </p>
                      </Alert>
                    </div>
                  )}

                  {schoolHolidaysOverlapWarning && (
                    <div className="form-group row">
                      <Alert className="d-flex gap-3" color="warning dark">
                        <AlertCircle />
                        <p>Warning: Delivery Period overlaps with School Holidays</p>
                      </Alert>
                    </div>
                  )}

                  <div className="form-group row" style={{ maxWidth: 400 }}>
                    <DataGrid
                      autoHeight
                      hideFooter
                      loading={false}
                      rows={calculations.publicHolidays.map((val, i) => ({ ...val, id: i }))}
                      columns={publicHolidayColumns}
                      disableSelectionOnClick
                    />
                  </div>
                  <br />
                  <div className="form-group row" style={{ maxWidth: 400 }}>
                    <DataGrid
                      autoHeight
                      hideFooter
                      loading={false}
                      rows={calculations.schoolHolidays.map((val, i) => ({ ...val, id: i }))}
                      columns={schoolHolidayColumns}
                      disableSelectionOnClick
                    />
                  </div>
                  <br />
                  <div className="form-group row" style={{ maxWidth: 400 }}>
                    <DataGrid
                      autoHeight
                      hideFooter
                      paginationMode="server"
                      loading={false}
                      rows={Object.entries(calculations.include)
                        .map(([key, val], i) => ({
                          id: i,
                          inc: val,
                          exc: calculations.exclude[key],
                          day: days[key],
                        }))
                        .concat([schoolDays])}
                      columns={schoolDayColumns}
                      pageSize={10}
                      rowCount={0}
                      rowsPerPageOptions={[10]}
                      disableSelectionOnClick
                    />
                  </div>
                </>
              )}
              {currentDeliveryRun !== undefined && (
                <div className="container" style={{ marginLeft: 0 }}>
                  <div className="row">
                    <div className="col-2" style={{ paddingLeft: 0 }}>
                      Last Calculated
                    </div>
                    <div className="col-10">
                      {`${currentDeliveryRun.last_calculated_user_name} ${currentDeliveryRun.last_calculated_user_surname}`}{' '}
                      {formatTime(currentDeliveryRun.last_calculated_datetime)}
                    </div>
                  </div>
                </div>
              )}

              {
                /* DISPLAY UPDATED TABLES */
                currentDeliveryRun && (
                  <>
                    {!currentDeliveryRun.is_finalised && (
                      <>
                        {currentDeliveryRun.updated_tables.length > 0 && (
                          <>
                            <div className="form-group row">
                              <Alert className="d-flex gap-3" color="warning dark">
                                <AlertCircle />
                                <p>
                                  Changes have been made to {currentDeliveryRun.updated_tables.join(', ')} after last
                                  calculation date.
                                </p>
                              </Alert>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </>
                )
              }

              {currentDeliveryRun !== undefined && (
                <div className="container" style={{ marginLeft: 0 }}>
                  <div className="row">
                    <div className="col-2" style={{ paddingLeft: 0 }}>
                      Progress Status
                    </div>
                    <div className="col-10">
                      {currentDeliveryRun.is_finalised
                        ? `Finalized (${currentDeliveryRun.finalised_user_name} ${
                            currentDeliveryRun.finalised_user_surname
                          } ${formatTime(currentDeliveryRun.finalised_by_datetime)})`
                        : `Draft`}
                    </div>
                  </div>
                </div>
              )}

              {!!deliveryRunId && (
                <div className="d-flex justify-content-start my-4">
                  <FormGroup>
                    <FormControlLabel
                      control={<Switch {...register('is_active')} checked={watch('is_active')} />}
                      label="Active Status"
                      sx={{ margin: 0 }}
                      labelPlacement="start"
                    />
                  </FormGroup>
                </div>
              )}
              <div className="d-flex gap-3">
                {!values?.is_finalised && (
                  <button
                    className="btn btn-primary"
                    disabled={dateError ? true : disableButton}
                    type={schoolHolidaysOverlapWarning ? 'button' : 'submit'}
                    onClick={
                      schoolHolidaysOverlapWarning
                        ? () => setHolidayWarningOpen(schoolHolidaysOverlapWarning)
                        : undefined
                    }
                  >
                    {deliveryRunId ? 'Save' : 'Create'}
                  </button>
                )}
                <button className="btn btn-primary" type="button" onClick={() => history.push('/delivery-runs/manage')}>
                  Cancel
                </button>
              </div>
              <br />
              {!!deliveryRunId ? (
                <>
                  <br />
                  <br />
                  <h5>Delivery Report</h5>

                  <div className="d-flex gap-3">
                    <button
                      className="btn btn-primary"
                      disabled={currentDeliveryRun?.is_finalised} // Disable if finalized
                      style={{
                        backgroundColor: currentDeliveryRun?.is_finalised ? '#d3d3d3' : '', // Gray out when disabled
                        color: currentDeliveryRun?.is_finalised ? '#6c757d' : '', // Darken text when disabled
                        cursor: currentDeliveryRun?.is_finalised ? 'not-allowed' : 'pointer', // Change cursor when disabled
                      }}
                      onClick={!currentDeliveryRun?.is_finalised ? handleExport : undefined} // Prevent click when disabled
                    >
                      Export Delivery Run (For Sage API)
                    </button>
                  </div>
                </>
              ) : (
                <div className="form-group row">
                  <Alert className="d-flex gap-3" color="secondary">
                    <AlertCircle /> <p>Save changes to view reports</p>
                  </Alert>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      <Dialog maxWidth="sm" TransitionProps={{ onEntered: () => {} }} open={holidayWarningOpen}>
        <DialogTitle>Warning: School Holidays Overlap</DialogTitle>
        <DialogContent dividers>
          {`Delivery Period overlaps with School Holidays:  ${calculations.schoolHolidays[0]?.date ?? ''} to ${
            calculations.schoolHolidays[calculations.schoolHolidays.length - 1]?.date ?? ''
          }`}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSubmit(onSave)}>Save Anyway</Button>
          <Button onClick={() => setHolidayWarningOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Modal
        isOpen={errorModalOpen}
        toggle={() => {
          setErrorModalOpen(false);
        }}
        size="lg"
        scrollable={true}
        centered={true}
      >
        <ModalHeader
          toggle={() => {
            setErrorModalOpen(false);
          }}
        >
          Error: Accounting Codes or SKU's needed.
        </ModalHeader>
        <ModalBody>
          <div className="card-body">
            {skusMissingAccountingCodes.length !== 0 && (
              <>
                <Typography variant="h6">A Sage accounting code is required for the following SKUs.</Typography>
                <DataGrid
                  getRowId={(row) => row.skuId}
                  autoHeight
                  hideFooter
                  rows={skusMissingAccountingCodes}
                  columns={[
                    {
                      field: 'skuId',
                      headerName: '',
                      flex: 1,
                      /**
                       * @param {Object} props
                       * @param {Object} props.row - The row data.
                       * @returns {React.ReactElement} A link to edit the SKU.
                       * @description
                       * This function renders a link to edit the SKU in a new tab.
                       * The link is shown as a VisibilityIcon. When clicked, the
                       * link opens a new tab with the SKU edit form.
                       */
                      renderCell: ({ row }) => {
                        return (
                          <Link
                            component="button"
                            onClick={() => window.open(`/skus/edit/${row.skuId}`, '_blank').focus()}
                          >
                            <VisibilityIcon />
                          </Link>
                        );
                      },
                    },
                    {
                      field: 'sku',
                      headerName: 'SKU',
                      flex: 3,
                    },
                  ]}
                />
              </>
            )}
            {missingSkus.length !== 0 && (
              <>
                <br />
                <Typography variant="h6">A SKU is required for the following SKU Types.</Typography>
                <DataGrid
                  getRowId={(row) => row.skuTypeId}
                  autoHeight
                  hideFooter
                  rows={missingSkus}
                  columns={[
                    {
                      field: 'skuTypeId',
                      headerName: '',
                      flex: 1,
                      renderCell: ({ row }) => {
                        return (
                          <Link
                            component="button"
                            onClick={() =>
                              window.open(`/sku-types/manage?skuTypeId=${row.skuTypeId}`, '_blank').focus()
                            }
                          >
                            <VisibilityIcon />
                          </Link>
                        );
                      },
                    },
                    {
                      field: 'skuType',
                      headerName: 'SKU Type',
                      flex: 3,
                    },
                  ]}
                />
              </>
            )}
            <br />
            <button className="btn btn-primary" onClick={() => setErrorModalOpen(false)}>
              Ok
            </button>
          </div>
        </ModalBody>
      </Modal>
    </Fragment>
  );
};

const publicHolidayColumns = [
  {
    field: 'date',
    headerName: 'Public Holidays',
    flex: 1,
  },
  {
    field: 'day',
    headerName: 'Day',
    flex: 1,
    valueFormatter: ({ value }) => days[value],
  },
];

const schoolHolidayColumns = [
  {
    field: 'date',
    headerName: 'School Holidays',
    flex: 1,
  },
  {
    field: 'day',
    headerName: 'Day',
    flex: 1,
    valueFormatter: ({ value }) => days[value],
  },
];

const schoolDayColumns = [
  {
    field: 'day',
    headerName: 'Days',
    flex: 1,
    sortable: false,
  },
  {
    field: 'inc',
    headerName: 'Incl',
    flex: 1,
    sortable: false,
    valueFormatter: ({ value }) => (value ? value : ''),
  },
  {
    field: 'exc',
    headerName: 'Excl',
    flex: 1,
    sortable: false,
    valueFormatter: ({ value }) => (value ? value : ''),
  },
];

const schoolPackSizesColumns = [
  {
    field: 'schoolName',
    headerName: 'School',
    headerAlign: 'left',
    flex: 1,
    /**
     * renderCell function for schoolName column.
     * @param {object} params - contains row data
     * @returns {ReactElement} - a ReactElement that is a link to the school's manage page
     * @example
     * <Link onClick={() => window.open(`/schools/manage?schoolId=${row.schoolId}`, '_blank').focus()}>
     *   {row.schoolName}
     * </Link>
     */
    renderCell: ({ row }) => {
      return (
        <Link onClick={() => window.open(`/schools/manage?schoolId=${row.schoolId}`, '_blank').focus()}>
          {row.schoolName}
        </Link>
      );
    },
  },
  {
    field: 'project',
    headerName: 'Project',
    headerAlign: 'left',
    flex: 1,
    // renderCell: ({ row }) => {
    //   return <Link onClick={() => window.open(`/projects/manage?projectId=${row.projectId}`, '_blank').focus()}>{row.project}</Link>
    // }
  },
  {
    field: 'menuLinks',
    headerName: 'Menu',
    headerAlign: 'left',
    flex: 2,
    /**
     * renderCell function for menuLinks column.
     * @param {object} params - contains row data
     * @returns {ReactElement} - a ReactElement that is a list of links to the menu's manage page
     * @example
     * <div>
     *   {row.menus.map((menu, index) => (
     *     <React.Fragment key={index}>
     *       <Link onClick={() => window.open(`/menu-details/manage?menuId=${menu.menuId}`, '_blank').focus()}>
     *         {menu.menuName}
     *       </Link>
     *       {index < row.menus.length - 1 && ', '}
     *     </React.Fragment>
     *   ))}
     * </div>
     */
    renderCell: ({ row }) => {
      return (
        <div>
          {row.menus.map((menu, index) => (
            <React.Fragment key={index}>
              <Link onClick={() => window.open(`/menu-details/manage?menuId=${menu.menuId}`, '_blank').focus()}>
                {menu.menuName}
              </Link>
              {index < row.menus.length - 1 && ', '}
            </React.Fragment>
          ))}
        </div>
      );
    },
  },
  {
    field: 'exceptionLinks',
    headerName: 'Exception',
    headerAlign: 'left',
    flex: 2,
    /**
     * RenderCell function for exceptionLinks column.
     *
     * This function generates a column of links for each exception in the row's exceptions array.
     * If an exception exists, it creates a link to the edit page for that exception.
     * The link displays the exception name, learner fraction percentage, menu, or number of SKU types, if available.
     *
     * @param {object} params - Contains row data, specifically the exceptions array.
     * @returns {ReactElement} - A React component that renders a list of exception links in a column.
     */
    renderCell: ({ row }) => {
      return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {row.exceptions.map((exception, index) =>
            exception.exception ? (
              <Link
                key={index}
                onClick={() => window.open(`/exceptions/edit/${exception.exception_id}`, '_blank').focus()}
              >
                {formatString(exception.exception ?? '')} (
                {exception.learnerFrac
                  ? `${exception.learnerFrac} %`
                  : null ?? exception.menu ?? exception.exceptionSkuTypes.length}
                )
              </Link>
            ) : null
          )}
        </div>
      );
    },
  },
];

/**
 * Default values for the form fields.
 * @property {string} school_calendar_id - The ID of the school calendar. Default: "".
 * @property {string} delivery_run_type_id - The ID of the delivery run type. Default: "".
 * @property {string} consumption_end_date - The end date for consumption. Default: "".
 * @property {string} consumption_start_date - The start date for consumption. Default: "".
 * @property {string} planned_delivery_end_date - The end date for planned delivery. Default: "".
 * @property {string} planned_delivery_start_date - The start date for planned delivery. Default: "".
 * @property {boolean} is_active - The active status of the form. Default: true.
 */
const defaultValues = {
  database: '',
  school_calendar_id: '',
  delivery_run_type_id: '',
  consumption_end_date: '',
  consumption_start_date: '',
  planned_delivery_end_date: '',
  planned_delivery_start_date: '',
  is_active: true,
};

/**
 * Validation Schema for form fields
 */
const validationSchema = Yup.object().shape({
  //* Validation for school_calendar_id field
  school_calendar_id: Yup.string()
    // Transform the value to NaN if it's less than or equal to 0
    .transform((value) => (parseInt(value) <= 0 ? NaN : value))
    // Error message if the value is not a string
    .typeError('Incorrect input')
    // Error message if the value is empty
    .required('Required'),

  //* Validation for delivery_run_type_id field
  delivery_run_type_id: Yup.string()
    // Transform the value to NaN if it's less than or equal to 0
    .transform((value) => (parseInt(value) <= 0 ? NaN : value))
    // Error message if the value is not a string
    .typeError('Incorrect input')
    // Error message if the value is empty
    .required('Required'),

  // consumption_end_date: Yup.date().typeError("invalid format").required("Required"),
  // consumption_start_date: Yup.date().typeError("invalid format").required("Required"),
  // planned_delivery_end_date: Yup.date().typeError("invalid format").required("Required"),
  // planned_delivery_start_date: Yup.date().typeError("invalid format").required("Required"),

  //mark these fields as required
  database: Yup.string().required(),
  consumption_end_date: Yup.string().required('Required'),
  consumption_start_date: Yup.string().required('Required'),
  planned_delivery_end_date: Yup.string().required('Required'),
  planned_delivery_start_date: Yup.string().required('Required'),
  is_active: Yup.boolean().required(),
});

/**
 * Calculates school holidays based on the response, from date, and to date.
 * If skip is true, returns default calculations.
 *
 * @param {object} response - The response object containing public holidays and terms.
 * @param {string} from - The start date.
 * @param {string} to - The end date.
 * @param {boolean} [skip=false] - Whether to skip the calculation and return default calculations.
 * @returns {object} - The calculated school holidays.
 */
function getSchoolHolidays(response, from, to, skip = false) {
  // If skip is true, return default calculations
  if (skip) return defaultCalcultions;

  try {
    // Create a clone of default calculations
    let f = new Date(from);
    let t = new Date(to);

    let holidays = response.ph;

    let terms = response.t.sort((a, b) => new Date(a.start_date).getTime() - new Date(a.start_date).getTime());

    //gets the terms and holidays within the specified date range

    let setTerms = [...terms];
    let setHolidays = [...holidays];

    let calcs = getSchoolDaysForConsumptionPeriod(structuredClone(defaultCalcultions), f, t, setTerms, setHolidays);

    return calcs;
  } catch (e) {
    console.error(e);
    return defaultCalcultions;
  }
}

function dateToFormattedString(d) {
  var yyyy = d.getFullYear().toString();
  var mm = (d.getMonth() + 1).toString(); // getMonth() is zero-based
  var dd = d.getDate().toString();

  return yyyy + '-' + (mm[1] ? mm : '0' + mm[0]) + '-' + (dd[1] ? dd : '0' + dd[0]);
}

/**
 * An object representing the counts for each weekday (Monday to Friday).
 * @typedef {Object} TempDays
 * @property {number} 1 - The count for Monday.
 * @property {number} 2 - The count for Tuesday.
 * @property {number} 3 - The count for Wednesday.
 * @property {number} 4 - The count for Thursday.
 * @property {number} 5 - The count for Friday.
 *
/**
 * An object representing the counts for each weekday (Monday to Friday), initialized with 0 for all weekdays.
 * @type {{1:number, 2:number, 3:number, 4:number, 5:number}}
 */
const temp_inc_days = Object.freeze({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });
const temp_exc_days = Object.freeze({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });

/**
 * An immutable object representing default calculations for school holidays and public holidays.
 * @typedef {Object} DefaultCalculations
 * @property {TempDays} exclude - An object holding the counts for excluded weekdays.
 * @property {TempDays} include - An object holding the counts for included weekdays.
 * @property {readonly []} publicHolidays - An empty array to store public holidays.
 * @property {readonly []} schoolHolidays - An empty array to store school holidays.
 */
/**
 * An immutable object representing default calculations for school holidays and public holidays.
 * @type {DefaultCalculations}
 */
const defaultCalcultions = Object.freeze({
  exclude: temp_inc_days,
  include: temp_exc_days,
  publicHolidays: Object.freeze([]),
  schoolHolidays: Object.freeze([]),
});

/**
 * Retrieves the terms that include a specified start and end date from the given array of terms.
 * @param {Array} terms - Array of terms objects. ENSURE DATE IS SORTED IN ASCENDING ORDER
 * @param {Date} startDate - Start date to check.
 * @param {Date} endDate - End date to check.
 * @returns {Array} - Array of terms that include the specified date range.
 */

/**
 * Filters an array of public holidays to include only holidays within a specified start and end date range.
 * @param {Array} publicHolidays - The array of public holidays to filter.
 * @param {Date} startDate - The start date of the range.
 * @param {Date} endDate - The end date of the range.
 * @returns {Array} - The filtered array of public holidays within the specified range.
 */
function getPublicHolidaysByDateRange(publicHolidays, startDate, endDate) {
  // Convert the start and end dates to UTC timestamps for comparison
  const startTimestamp = startDate.getTime();
  const endTimestamp = endDate.getTime();

  // Filter the public holidays based on the date range
  const filteredHolidays = publicHolidays.filter((holiday) => {
    const holidayTimestamp = new Date(holiday.date).getTime();
    return holidayTimestamp >= startTimestamp && holidayTimestamp <= endTimestamp;
  });

  return filteredHolidays;
}

/**
 * Calculates the number of school days and holidays within a specified date range,
 * taking into account the school terms and public holidays.
 *
 * @param {DefaultCalculations} calculations - The initial calculations object.
 * @param {Date} start - The start date of the period.
 * @param {Date} end - The end date of the period.
 * @param {Array} terms - An array of school terms.
 * @param {Array} holidays - An array of public holidays.
 * @returns {Object} - The modified calculations object with updated information.
 */
function getSchoolDaysForConsumptionPeriod(calculations, start, end, terms, holidays) {
  let returnValue = structuredClone(calculations);
  const publicHolidaysInRange = getPublicHolidaysByDateRange(holidays, start, end);

  let startDate = start;
  let endDate = end;

  for (
    let currentDate = startDate; //set current date to start
    currentDate.setHours(0, 0, 0, 0) <= endDate.setHours(0, 0, 0, 0); //run while current date is <= end date
    currentDate.setDate(currentDate.getDate() + 1) //on each iteration, set current date to the day after
  ) {
    //check if current date is a weekday
    const day = currentDate.getDay();

    //if a weekend, go to the next iteration of the loop
    if (day === 0 || day === 6) {
      continue;
    }

    let isSchoolDay = false;

    //check if day is a school day
    for (let term of terms) {
      //if the current date is within this term

      //dates appear to be zero based, so add 1 here
      let termStartDate = new Date(term.start_date);
      termStartDate.setDate(termStartDate + 1);

      let termEndDate = new Date(term.end_date);
      termEndDate.setDate(termEndDate + 1);
      //new Date(term.start_date)

      if (
        currentDate.setHours(0, 0, 0, 0) >= new Date(term.start_date).setHours(0, 0, 0, 0) && //current >= start date
        currentDate.setHours(0, 0, 0, 0) <= new Date(term.end_date).setHours(0, 0, 0, 0)
      ) {
        //current <= end date
        isSchoolDay = true;
        break; // break out of the loop
      }
    } //Dayjs

    //if not a term day it is a school holiday, save it to school holiday, go to the next day
    if (!isSchoolDay) {
      //add it to the school holidays
      returnValue.schoolHolidays.push({ date: dateToFormattedString(currentDate), day: day });
      continue;
    }

    //check if day is a public holiday
    let isPublicHoliday = false;
    for (let publicHoliday of publicHolidaysInRange) {
      if (currentDate.setHours(0, 0, 0, 0) === new Date(publicHoliday.date).setHours(0, 0, 0, 0)) {
        isPublicHoliday = true;
        //add the public holiday
        returnValue.publicHolidays.push({ date: dateToFormattedString(currentDate), day: day });
        break; //break out of loop
      }
    }

    //if a normal school day, add to the count of school days
    if (isSchoolDay && !isPublicHoliday) {
      returnValue.include[day] += 1;
    }

    //if a normal school day, but a public holiday, it's an excluded day
    if (isSchoolDay && isPublicHoliday) {
      returnValue.exclude[day] += 1;
    }
  }
  return returnValue;
}

function formatTime(dateString) {
  const dateObject = new Date(dateString);

  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false, // Use 24-hour format
  };

  let formattedDate = dateObject.toLocaleDateString('en-GB', options);

  const parts = formattedDate.split(', ');
  const [dayMonthYear, time] = parts;
  const [day, month, year] = dayMonthYear.split('/');
  const rearrangedDate = `${year}-${month}-${day} ${time}`;
  return rearrangedDate;
}

const formatString = (str) => {
  return str
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default Manage;
